import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ServerError } from '@omq/shared';
import {
  isFocusVisible,
  isMobileDevice,
  setStyle,
  useConfigContext,
  useEventContext,
} from '@omq/shared';
import type { Placeholder, TimeoutID } from '@omq/types';
import { ConfigCategoryDisplayTypes, ConfigMobileSearchDisplayTypes } from '@omq/types';
import type { HelpCategory, HelpPage, HelpSearchParams } from '../api/help';
import type { HelpURLParams } from '../stores/url-store';

import { CategoryNavigation } from '../components/navigation/category-navigation';
import { Search, SearchRefType } from '../components/search/search';
import { CategoryGrid } from '../components/category-grid/category-grid';
import { QuestionList } from '../components/question-list/question-list';
import { useHelpPage } from '../hooks/help-page';
import { useURLParams } from '../hooks/url-params';
import { ErrorView } from './error-view';
import { defaultSearchResult, useAutoComplete } from '../hooks/auto-complete';
import { useFaqStructuredData } from '../hooks/faq-structured-data';
import { keepAutoComplete } from '../utils/keep-auto-complete';
import { useCanonical } from '../hooks/canonical';
import { useDocumentTitle } from '../hooks/document-title';
import { HelpEvents } from '../events/help-events';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@omq/shared/src/hooks/use-focus-trap';
import { LIST_ANNOUNCEMENT_DELAY, STATUS_RESET_GAP } from '../constants/announcement';

/**
 * Type for component properties.
 */
type HelpViewProps = {
  initialHelpPage?: HelpPage;
  structuredDataIsEnabled?: boolean;
  initialSearchValue: string;
  keepFocus?: boolean;
  placeholder: Placeholder;
  portalElement?: HTMLElement | null;
  children?: ReactNode;
  initialCategoryId?: number;
};

/**
 * Helper function.
 * Find help category by id.
 *
 * @param {Array<HelpCategory>} categories - array of help categories
 * @param {number|null} selectedCategory - selected category
 *
 * @returns {HelpCategory|null}
 */
function getSelectedHelpCategory(
  categories: Array<HelpCategory>,
  selectedCategory: number | null,
): HelpCategory | null {
  if (selectedCategory == null) {
    return null;
  }

  return categories.find((category) => category.id === selectedCategory) || null;
}

// the mobile search modal is a phone screen that also holds the result list
const MODAL_AUTO_COMPLETE_LIMIT = 3;

/**
 * Read how far the host page is scrolled.
 *
 * @returns {number}
 */
function readScrollTop(): number {
  return document.documentElement?.scrollTop || document.body.scrollTop;
}

/**
 * Component description.
 *
 * @param {HelpViewProps} props - Component properties
 *
 * @author Florian Walch
 * @since 9.5
 *
 * @returns {JSX.Element}
 */
export function HelpView({
  initialHelpPage,
  structuredDataIsEnabled,
  initialSearchValue,
  keepFocus = false,
  placeholder,
  portalElement,
  children,
  initialCategoryId,
}: HelpViewProps): JSX.Element {
  const config = useConfigContext();
  const useSearchPopup =
    isMobileDevice() && config.mobileSearchDisplayType === ConfigMobileSearchDisplayTypes.POPUP;
  const searchInputRef = useRef<SearchRefType>();

  const [searchValue, setSearchValue] = useState<string>(initialSearchValue);
  const [currentHelpPage, setCurrentHelpPage] = useState<number>(-1);
  const [currentSearchPage, setCurrentSearchPage] = useState<number>(-1);

  // get urlParams
  const [urlParams, updateURL] = useURLParams((urlParams: HelpURLParams) => {
    setSelectedCategory(urlParams.category as number);
    setSelectedQuestion(urlParams.question as number);
  });

  // set up states
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(
    urlParams.question as number,
  );
  const [selectedCategory, setSelectedCategory] = useState<number | null>(
    (urlParams.category as number) ?? initialCategoryId ?? null,
  );
  // get current help page
  const [helpPage, error] = useHelpPage(
    selectedCategory,
    currentHelpPage,
    initialHelpPage?.categories != null || initialHelpPage?.questions != null
      ? initialHelpPage
      : null,
    placeholder,
  );
  useFaqStructuredData(helpPage, structuredDataIsEnabled);

  // one-shot URL sync: when the category came from the integration preset
  // (URL had no category on mount), rewrite the URL once the matching category
  // has been resolved via the loaded help page. updateURL already no-ops when
  // isUrlSyncActive is disabled, so no extra gating is needed here.
  const didInitialUrlSyncRef = useRef<boolean>(
    urlParams.category != null || initialCategoryId == null,
  );

  useEffect(() => {
    if (didInitialUrlSyncRef.current) {
      return;
    }
    const currentCategory = helpPage.categoryPath[helpPage.categoryPath.length - 1];
    if (currentCategory == null || currentCategory.id !== initialCategoryId) {
      return;
    }
    didInitialUrlSyncRef.current = true;
    updateURL(currentCategory.url, config.urlPatternType);
  }, [helpPage.categoryPath, initialCategoryId, updateURL, config.urlPatternType]);

  const [currentError, setCurrentError] = useState<ServerError>(error);

  // get the search result
  // memoize search params, to prevent infinite search
  const searchParams = useMemo(() => {
    const params: HelpSearchParams = {};
    if (selectedCategory != null) {
      params.category = selectedCategory.toString();
    }

    // add page as search param
    if (currentSearchPage !== -1) {
      params.page = currentSearchPage;
    }

    return params;
  }, [selectedCategory, currentSearchPage]);

  const { questions, autoComplete, nextPage: nextSearchPage } = useAutoComplete(
    searchValue,
    placeholder,
    searchParams,
  );

  const searchIsActive = searchValue !== '';
  const isModalView = searchIsActive && useSearchPopup;
  const currentQuestions = searchIsActive ? questions : helpPage.questions;

  const currentCategory = helpPage.categoryPath[helpPage.categoryPath.length - 1];

  const headline =
    currentCategory == null ? config.loc('faq') : config.loc('faq_category', currentCategory.name);

  const searchPlaceholder =
    currentCategory == null
      ? null
      : config.loc('search_placeholder_category', currentCategory.name);

  // define if headline is visible
  // - not in modal view
  // - only if search value is empty
  const headlineIsVisible = !isModalView && searchValue === '';

  // an empty list is an empty category as long as no search is running
  const isEmptyCategory = searchValue === '' && questions.length === 0;

  // Text of the polite summary for the currently rendered question list.
  //
  // Replaces the aria-live region that used to sit on the portal element and
  // announced the raw question titles. That region had two defects
  // (server-2562): the heading with the filter context was rendered outside
  // of it, so the titles were announced without saying what they belonged to,
  // and with aria-relevant="additions" every entry whose DOM node React
  // reused between the old and the new list was silently skipped.
  /**
   * Build the text of the polite list summary.
   *
   * @param {boolean} headlineWasRead - true when the focus was just moved onto
   *   the heading, so the screen reader has already read it
   *
   * @returns {string} text to write into the live region
   */
  const buildListStatus = (headlineWasRead: boolean): string => {
    if (currentQuestions.length === 0) {
      // mirror the text the list itself renders for an empty result
      return config.loc(isEmptyCategory ? 'emptyCategory' : 'emptylist');
    }

    const countStatus = config.loc('aria_questions_found', String(currentQuestions.length));

    // Outside of a search the headline carries the filter context ("frequently
    // asked questions in <category>"), which is what makes the announcement
    // understandable after a category filter was applied. A tenant may
    // configure an empty headline - then the count stands on its own instead
    // of being prefixed with a stray separator.
    const withContext = headlineIsVisible && headline !== '' && !headlineWasRead;

    return withContext ? `${headline}, ${countStatus}` : countStatus;
  };

  const [listStatus, setListStatus] = useState<string>('');
  const listStatusTimerRef = useRef<TimeoutID | null>(null);
  const listStatusApplyTimerRef = useRef<TimeoutID | null>(null);

  // Set by the handlers that change the list on purpose, and never cleared.
  // Without it, ANY page load would speak: a question deep link mounts this
  // view with no initial page, so useHelpPage fetches on mount, and
  // `setPlaceholder` (part of the widget's public API) is in that hook's
  // dependencies too - both would produce an unprompted utterance.
  //
  // It must NOT be consumed per announcement: a single gesture can produce two
  // responses (a query past MIN_LENGTH makes useDelayedSearch issue a second
  // request for the final text via needsRefresh), and a consumed flag made the
  // second arrival cancel the first one's pending announcement without arming
  // its own - the search then went unannounced entirely (server-2562 review).
  const hasListInteractionRef = useRef<boolean>(false);

  // Guards against re-announcing a state that was already announced, while
  // still allowing an identical TEXT to be announced again when the underlying
  // result really changed.
  const lastAnnouncedTokenRef = useRef<string | null>(null);

  // Result heading. Focusable via script only - it receives the focus when a
  // category filter removes the tile that was activated (see onCategoryChange).
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const restoreFocusToHeadlineRef = useRef<boolean>(false);
  const headlineTookFocusRef = useRef<boolean>(false);

  // Declared BEFORE the announcement effect on purpose: effects run in
  // declaration order, and the announcement needs to know whether the heading
  // was just read out by the focus move, so it does not repeat it.
  //
  // Deps are `helpPage` ONLY. `currentQuestions` would change identity on the
  // intermediate render right after onCategoryChange clears the search value
  // (the source flips from the search result to the page), which resolved this
  // effect while the activated control was still mounted and focused - the flag
  // was then cleared and the focus was never restored once the control did go
  // away (server-2562 review).
  useEffect(() => {
    if (!restoreFocusToHeadlineRef.current) {
      return;
    }

    restoreFocusToHeadlineRef.current = false;

    const activeElement = document.activeElement;

    // the activated tile is still there and still has the focus - the screen
    // reader announces its new pressed state, nothing left to do
    if (activeElement != null && activeElement !== document.body) {
      return;
    }

    // An empty configured headline renders aria-hidden, so focusing it would
    // put the focus somewhere a screen reader says nothing about. Fall back to
    // the search input, which always announces itself.
    if (headlineRef.current != null && headline !== '') {
      headlineRef.current.focus();
      headlineTookFocusRef.current = true;
    } else if (!useSearchPopup && searchInputRef.current != null) {
      searchInputRef.current.focus();
    }
  }, [helpPage]);

  // The second announcement phase can still be pending inside STATUS_RESET_GAP.
  // Cancelled on unmount ONLY - doing it in the announcement effect's cleanup
  // would cancel it on every data change, which is how a pending announcement
  // got dropped without a replacement before (server-2562 review).
  useEffect(
    () => () => {
      if (listStatusApplyTimerRef.current != null) {
        clearTimeout(listStatusApplyTimerRef.current);
        listStatusApplyTimerRef.current = null;
      }
    },
    [],
  );

  // Which category the currently loaded page actually belongs to. `null` is the
  // root, which is also what `selectedCategory` holds there.
  const loadedCategoryId = currentCategory?.id ?? null;

  // Is the displayed data the SETTLED answer to what is currently selected?
  //
  // Page: the loaded page carries the category it belongs to, so it can be
  // compared with the selection directly.
  // Search: the result carries no query, but the one transient state is
  // identifiable - useDelayedSearch installs `defaultSearchResult` itself,
  // synchronously and without a request, whenever the search value becomes
  // empty. A real "nothing found" response is a different object.
  const pageMatchesSelection = loadedCategoryId === (selectedCategory ?? null);
  const searchWasReset = questions === defaultSearchResult.questions;
  const listIsSettled = searchIsActive ? !searchWasReset : pageMatchesSelection;

  // Token describing WHAT IS RENDERED, deliberately built from resolved data
  // only - no `searchValue`.
  //
  // Including the query made this input-driven again: from the second search on,
  // useDelayedSearch leaves the previous response in place while the next
  // request is in flight, so a keystroke passed the settled check (the result is
  // not the reset sentinel) and armed the timer. If the new response then took
  // longer than the delay, the PREVIOUS query's count was announced as the
  // answer to the new one. The search result carries no query, so nothing here
  // can compensate for that - the trigger has to stay data-driven
  // (server-2562 review).
  const settleToken = [
    searchIsActive ? 'search' : 'page',
    selectedCategory ?? '',
    loadedCategoryId ?? '',
    currentQuestions.map((question) => question.id).join(','),
  ].join('|');

  // Announce the list once the view has SETTLED.
  //
  // The trigger must not be the user's input: `searchValue` and
  // `selectedCategory` change in the same tick as the keystroke or click, while
  // `questions` / `helpPage` still hold the PREVIOUS result, so announcing
  // there reports the old category - or "no entry found" for a first search -
  // as the answer to the new action (server-2562 review).
  useEffect(() => {
    // never speak before the visitor has asked the list for anything
    if (!hasListInteractionRef.current) {
      return;
    }

    // what is on screen is still on its way - a later change announces it
    if (!listIsSettled) {
      return;
    }

    if (lastAnnouncedTokenRef.current === settleToken) {
      return;
    }

    listStatusTimerRef.current = self.setTimeout(() => {
      lastAnnouncedTokenRef.current = settleToken;

      // When the focus was just moved onto the heading, the screen reader has
      // already read it - repeating it verbatim would say the same sentence
      // twice in a row, so only the count follows.
      const status = buildListStatus(headlineTookFocusRef.current);
      headlineTookFocusRef.current = false;

      // Clear first, then set: a polite live region does not re-announce text
      // identical to what it already holds, so two different searches that
      // happen to yield the same count would otherwise be silent.
      setListStatus('');
      listStatusApplyTimerRef.current = self.setTimeout(
        () => setListStatus(status),
        STATUS_RESET_GAP,
      );
    }, LIST_ANNOUNCEMENT_DELAY);

    return () => {
      if (listStatusTimerRef.current != null) {
        clearTimeout(listStatusTimerRef.current);
        listStatusTimerRef.current = null;
      }
    };

    // driven by the settle token: it already folds in every input and every
    // arriving response, and restarting the timer on each change is what makes
    // intermediate states inaudible
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settleToken, listIsSettled]);

  // handle search close
  const onSearchClose = () => {
    // Closing the search swaps the result list back to the full category list,
    // which is a list change like any other. This handler does NOT route
    // through onSearchChange, so it has to mark the interaction itself
    // (server-2562 review).
    hasListInteractionRef.current = true;
    setSearchValue('');

    // clear/hide auto complete
    if (searchInputRef.current != null) {
      searchInputRef.current.clearAutoComplete();
      searchInputRef.current.focus(isModalView ? { preventScroll: true } : undefined);
    }
  };

  // create a focus trap for the modal view which is only active when there is no selected question and modal is active
  const focusTrapRef = useFocusTrap(isModalView && selectedQuestion === null, onSearchClose);

  const helpWrapperClassName = `${config.generateClassName('help-wrapper')} ${
    isModalView ? `${config.generateClassName('modal__panel')} mobile-search-input` : ''
  }`;

  // add the canonical link for the selected question,
  // fall back to the deepest resolved category when no question is selected
  useCanonical(
    selectedQuestion == null
      ? null
      : currentQuestions.find((q) => q.id === selectedQuestion) || null,
    helpPage.categoryPath?.[helpPage.categoryPath.length - 1] ?? null,
    config.isCanonicalActive,
  );

  // update document.title — question body when a question is selected, else current category name, else null on bare index
  useDocumentTitle(
    selectedQuestion != null
      ? currentQuestions.find((q) => q.id === selectedQuestion)?.body ?? null
      : helpPage.categoryPath[helpPage.categoryPath.length - 1]?.name ?? null,
    config.titlePattern,
  );

  // fire faq categories loaded event when categories change
  const pageEvent = useEventContext<HelpEvents>();
  useEffect(() => {
    pageEvent.dispatchFAQCategoriesLoaded(helpPage.categories);
  }, [helpPage.categories]);

  // store latest body position style, its inline `top`, and the scroll offset we
  // froze the host page at — all three are needed to put it back untouched
  const lastBodyPosition = useRef(null);
  const lastBodyTop = useRef<string | null>(null);
  const lockedScrollTop = useRef(0);
  // the offset taken just before the render that opens the modal, see onSearchChange
  const scrollTopBeforeModal = useRef<number | null>(null);
  const initialViewportHeight = useRef(window.visualViewport?.height || 0);
  const searchViewRef = useRef(null);

  /**
   * BUGFIX Safari server-5742
   *
   * Since safari does not prevent scrolling of elements behind
   * modal view, we do it here.
   */
  useEffect(() => {
    const visualViewportIsAvailable = window.visualViewport != null;

    // get the body element
    const body = document.body;
    if (body == null) {
      return;
    }

    // prevent scrolling while search input has focus
    function handleTouchMove(evt: TouchEvent) {
      evt.preventDefault();
    }

    // put the host page back exactly as we found it: its own `position` and `top`,
    // and the scroll offset that going `position: fixed` threw away. Clears the
    // stored state so a second call (effect re-run plus cleanup) is a no-op.
    function restoreBodyScroll() {
      if (lastBodyPosition.current == null) {
        return;
      }

      body.style.position = lastBodyPosition.current;
      body.style.top = lastBodyTop.current ?? '';

      // The host may use smooth scrolling. Legacy browsers without it may not
      // support scrollTo options either, so retain the scrollTop fallback.
      if (
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('scroll-behavior', 'smooth')
      ) {
        window.scrollTo({
          top: lockedScrollTop.current,
          behavior: 'instant' as unknown as ScrollBehavior,
        });
      } else {
        if (document.documentElement != null) {
          document.documentElement.scrollTop = lockedScrollTop.current;
        }
        body.scrollTop = lockedScrollTop.current;
      }

      lastBodyPosition.current = null;
      lastBodyTop.current = null;
      lockedScrollTop.current = 0;
    }

    // handle viewport changes (mobile keyboard opens, and
    // changes the size of visible part of the screen)
    function handleViewportChanges() {
      const modal = searchViewRef.current;

      // if there is no modal, or search is not a modal,
      // no further action required
      if (modal == null || !isModalView) {
        return;
      }

      // get new height and offset
      const { height, offsetTop } = window.visualViewport;

      // if viewport is smaller & offsetTop is higher than 0
      // it means, keyboard is open, and viewport has been pushed outside
      // the screen
      if (initialViewportHeight.current > height && offsetTop > 0) {
        // set modal to the height if the available space
        // and move it to the bottom
        setStyle(modal, {
          bottom: 0,
          height: `${height - 30}px`,
          width: '100%',
          top: 'unset',
        });

        // disable scrolling
        // { passive: false } is required: chrome (android) treats document-level
        // touchmove listeners as passive by default, which makes preventDefault()
        // a no-op and logs a browser intervention (server-2531)
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
      } else {
        // keyboard is closed, reset styles
        setStyle(modal, {
          bottom: 0,
          top: null,
          height: 'auto',
          width: '100%',
        });

        // enable scrolling
        document.removeEventListener('touchmove', handleTouchMove);
      }
    }

    if (visualViewportIsAvailable) {
      // add listener to detect viewport changes (virtual keyboard open/close)
      window.visualViewport.addEventListener('resize', handleViewportChanges);
    }

    // if modal view is open
    if (isModalView) {
      // cache current style and the offset the page is scrolled to
      lastBodyPosition.current = body.style.position;
      lastBodyTop.current = body.style.top;
      lockedScrollTop.current = scrollTopBeforeModal.current ?? readScrollTop();
      scrollTopBeforeModal.current = null;

      // set body position to fixed
      // which disables scrolling for the entire site
      body.style.position = 'fixed';

      // shift the page up by however far it was scrolled, so it stays visually put.
      // A flat '0px' made the host page jump to the top whenever the search opened,
      // and the offset was gone for good afterwards (server-2730).
      body.style.top = `-${lockedScrollTop.current}px`;

      if (visualViewportIsAvailable) {
        handleViewportChanges();
      }
    } else {
      // if modal view has been closed
      restoreBodyScroll();
    }

    // clean up
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);

      if (visualViewportIsAvailable) {
        window.visualViewport.removeEventListener('resize', handleViewportChanges);
      }

      // restore the previous body-style if there was one.
      restoreBodyScroll();

      // clean up the modal window
      const modal = searchViewRef.current;

      // if there is no modal, or search is not a modal,
      // no further action required
      if (modal == null || !isModalView) {
        return;
      }

      // keyboard is closed, reset styles
      setStyle(modal, {
        bottom: 0,
        top: null,
        height: 'auto',
        width: '100%',
      });
    };
  }, [isModalView]);

  /**
   * BUGFIX server-2730
   *
   * The search modal used to render where the widget is embedded, so a host page that
   * wraps our container in a stacking context (position + z-index, e.g. a <main> at
   * z-index 9) capped the panel's z-index and let the page's own chrome paint over the
   * overlay. Render it into a container on <body> instead, the way the answer modal
   * (shared Modal) already does. Only the modal path moves — the inline markup is
   * untouched, so `.omq-help .omq-modal__panel` and the rest of our CSS still match.
   * Host CSS scoped to the embed container (`#omq-help-container ...`) does not reach
   * the modal any more; theme tokens do, they are declared on :root.
   */
  const modalContainerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Get the body-level container of the modal view, creating it on first use.
   *
   * @returns {HTMLDivElement}
   */
  const getModalContainer = (): HTMLDivElement => {
    if (modalContainerRef.current == null) {
      const container = document.createElement('div');
      // classList.add throws on an empty string, so fall back like createModalContainer
      container.classList.add(config.cssIdentifier || 'omq');
      modalContainerRef.current = container;
    }

    return modalContainerRef.current;
  };

  // whether the last committed view was the modal, to tell a close from the first render
  const wasModalViewRef = useRef(false);

  useLayoutEffect(() => {
    const wasModalView = wasModalViewRef.current;
    wasModalViewRef.current = isModalView;

    const container = isModalView ? getModalContainer() : null;
    if (container != null) {
      document.body.appendChild(container);
    }

    // Switching between the inline tree and the portal re-creates the search input, in
    // both directions, so hand focus to the new one. A layout effect still runs inside
    // the keystroke or click that caused the switch (React 17 flushes it synchronously),
    // a passive effect would run in a later task. That is the best chance for a mobile
    // browser to keep its keyboard open - not verified on devices, and an IME
    // composition in progress is still cut off by the swap.
    if ((isModalView || wasModalView) && searchInputRef.current != null) {
      searchInputRef.current.focus(wasModalView ? { preventScroll: true } : undefined);
    }

    if (container == null) {
      return undefined;
    }

    return () => {
      if (container.parentNode != null) {
        container.parentNode.removeChild(container);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalView]);

  if (currentError != null) {
    return <ErrorView errorMessage={config.loc(`_error.${currentError.errorType}`)} />;
  }

  // handle category selections
  const onCategoryChange = (category) => {
    // cancel any pending initial URL sync so it can't fire after a user action
    didInitialUrlSyncRef.current = true;
    hasListInteractionRef.current = true;
    setSearchValue('');
    setSelectedQuestion(null);
    setCurrentHelpPage(-1);

    // If no category is selected, reset
    if (category === null) {
      updateURL('/', config.urlPatternType);
      setSelectedCategory(null);
    }
    // If the currently selected category is clicked again, it will be toggled off
    else if (category.id === selectedCategory) {
      const { parent } = category;

      // If it has no parent category, none will be selected
      if (parent === null) {
        updateURL('/', config.urlPatternType);
      }
      // If it has a parent category, that will be selected
      else {
        const parentCategoriesArray = helpPage.categories.find((innerArray) =>
          innerArray.some((obj) => obj.id === parent),
        );

        // If the parent category is not found, the URL will be reset
        if (parentCategoriesArray == null) {
          updateURL('/', config.urlPatternType);
          setSelectedCategory(null);
          return;
        }

        const parentCategory = parentCategoriesArray.find((obj) => obj.id === parent);
        updateURL(parentCategory.url, config.urlPatternType);
      }
      setSelectedCategory(parent);
    }
    // Otherwise, the new category is simply selected
    else {
      setSelectedCategory(category.id);
      updateURL(category.url, config.urlPatternType);
    }

    // Keyboard focus must stay on the element that was just activated: the
    // category tiles are toggle buttons, and a screen reader only announces
    // their new pressed state while they keep the focus. Pulling the focus
    // into the search input instead made the filter change inaudible and
    // announced the combobox in its place (server-2562).
    //
    // Pointer activation keeps the previous behaviour - the search input is
    // focused so the next query can be typed right away. `false` is passed as
    // the fallback on purpose: where `:focus-visible` is unsupported we must
    // keep the pointer behaviour, because the helper's default of `true` would
    // route every mouse click through the keyboard branch.
    const activeElement = document.activeElement;
    const keepFocusOnTrigger =
      activeElement != null &&
      activeElement !== document.body &&
      isFocusVisible(activeElement, false);

    if (keepFocusOnTrigger) {
      // the tile can be replaced by the sub categories of the new selection
      // (categoryDisplayType DISAPPEAR); the headline then takes the focus
      restoreFocusToHeadlineRef.current = true;
      return;
    }

    // set focus to input field after navigation
    // only on desktop (never do autofocus on mobile screens)
    if (!useSearchPopup && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // handle search input changes
  const onSearchChange = (value) => {
    // The first character opens the modal, which takes the widget out of the page flow.
    // The page gets shorter and the browser clamps its scroll offset before any effect
    // runs, so the offset to freeze and later restore has to be read now.
    if (useSearchPopup && !searchIsActive && value !== '') {
      scrollTopBeforeModal.current = readScrollTop();
    }

    // reset search page
    setCurrentSearchPage(-1);
    hasListInteractionRef.current = true;
    setSearchValue(value);
  };

  // Set the question as selected and lazy load its answers
  const onQuestionClick = (question) => {
    // cancel any pending initial URL sync so it can't fire after a user action
    didInitialUrlSyncRef.current = true;

    // clear/hide auto complete
    if (searchInputRef.current != null) {
      searchInputRef.current.clearAutoComplete();
    }

    const category = getSelectedHelpCategory(helpPage.categoryPath, selectedCategory);

    updateURL(
      question != null ? question.url : category != null ? category.url : '/',
      config.urlPatternType,
    );

    setSelectedQuestion(question?.id ?? null);
  };

  // handle more button clicks
  const onMoreQuestionsClick = (event) => {
    event.preventDefault();

    // the appended entries change the list, so the new total is announced
    hasListInteractionRef.current = true;

    // set next current page depending on search state
    searchIsActive ? setCurrentSearchPage(nextSearchPage) : setCurrentHelpPage(helpPage.nextPage);
  };

  // more button is visible if there is a next page
  // nextPage value depends on search flag - use nextPage from search result or from help page
  const moreButtonIsVisible = searchIsActive ? nextSearchPage !== -1 : helpPage.nextPage !== -1;

  let categoriesGrids = null;

  // default behaviour
  if (config.categoryDisplayType === ConfigCategoryDisplayTypes.DISAPPEAR) {
    /* Do not show categories during search or if they are empty */
    if (!searchIsActive) {
      categoriesGrids = (
        <section className={config.generateClassName('help-container')}>
          <CategoryGrid
            categories={helpPage.categories[helpPage.categoryPath.length]}
            displayType={currentCategory == null ? 'large' : 'small'}
            onCategorySelection={onCategoryChange}
          />
        </section>
      );
    }
  } else if (config.categoryDisplayType === ConfigCategoryDisplayTypes.PERMANENT) {
    categoriesGrids = (
      <section className={config.generateClassName('help-container')}>
        {helpPage.categories.map((value, index) => {
          return (
            <CategoryGrid
              key={index}
              categories={value}
              selectedCategory={helpPage.categoryPath[index] || null}
              displayType={index === 0 ? 'large' : 'small'}
              onCategorySelection={onCategoryChange}
            />
          );
        })}
      </section>
    );
  }

  const helpTree = (
    <>
      {/*show modal backdrop when modal is active*/}
      {isModalView && <div className={config.generateClassName('modal')} />}

      <div
        ref={focusTrapRef}
        className={`${config.generateClassName('help')} notranslate`}
        translate="no">
        <span id={`${config.cssIdentifier}-help-title`} hidden>
          {helpPage.categoryPath[helpPage.categoryPath.length - 1]?.name ?? ''}
        </span>
        <div className={helpWrapperClassName} ref={searchViewRef}>
          {/* Do not show navigation in modal views */}
          {!isModalView && (
            <section className={config.generateClassName('help-container')}>
              <CategoryNavigation
                categories={helpPage.categoryPath}
                onCategorySelection={onCategoryChange}
              />
            </section>
          )}

          <section className={config.generateClassName('help-container')}>
            <Search
              value={searchValue}
              ref={searchInputRef}
              placeholder={searchPlaceholder}
              autoFocus={!useSearchPopup && (keepFocus || config.isAutoFocusActive)}
              autoCompleteItems={
                isModalView ? autoComplete.slice(0, MODAL_AUTO_COMPLETE_LIMIT) : autoComplete
              }
              onChange={onSearchChange}
              onCloseClick={searchIsActive ? onSearchClose : null}
            />
          </section>

          {/*do not show categories when mobile and search is active*/}
          {!(useSearchPopup && searchIsActive) && categoriesGrids}

          <section className={config.generateClassName('help-container')}>
            {/* No headline in modal pane */}
            {headlineIsVisible && (
              <h2
                ref={headlineRef}
                tabIndex={-1}
                aria-hidden={headline === ''}
                className={config.generateClassName('question-list-headline')}>
                {headline}
              </h2>
            )}

            {/*polite summary of the rendered question list - gives a screen
               reader the filter context and the amount of results after a
               category filter or a search*/}
            <div
              className={config.generateClassName('question-list-status')}
              role="status"
              aria-live="polite">
              {listStatus}
            </div>

            {/*render portal target element given from parent element*/}
            {children}

            {/*render QuestionList into the portal element which is given above in children*/}
            {portalElement != null &&
              createPortal(
                <QuestionList
                  questions={currentQuestions}
                  selectedQuestionId={selectedQuestion}
                  onQuestionClick={onQuestionClick}
                  isEmptyCategory={isEmptyCategory}
                  placeholder={placeholder}
                  onError={setCurrentError}
                  currentCategory={selectedCategory}
                />,
                portalElement,
              )}
            {moreButtonIsVisible && (
              <a
                tabIndex={0}
                title={config.loc('more_questions')}
                className={config.generateClassName('more-questions')}
                onMouseDown={(evt) =>
                  keepAutoComplete(evt, config.generateClassName('search__input'))
                }
                onClick={onMoreQuestionsClick}
                onKeyPress={(evt) => {
                  if (evt.key === 'Enter' || evt.key === ' ') {
                    onMoreQuestionsClick(evt);
                  }
                }}>
                {config.loc('more_questions')}
              </a>
            )}
          </section>
        </div>
      </div>
    </>
  );

  // in modal view the whole tree lives on <body>, where no host stacking context
  // can bury it; everywhere else it stays inline exactly as before
  return isModalView ? createPortal(helpTree, getModalContainer()) : helpTree;
}
